import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, EMPTY, forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { createRequestOption, SearchWithPagination } from '@cupoui/shared/util/request-util';
import { IUser } from './user.model';
import { CustomerProfiles } from '@cupoui/shared/model/customerProfiles';
import { SERVER_API_URL } from '../../app.constants';
import moment from 'moment';
import { RoleService } from '@cupoui/core/user/role.service';
import { CustomerProfilePermission } from '@cupoui/shared/model/customerProfilePermission';
import { Permission } from '@cupoui/shared/model/permission';

@Injectable({ providedIn: 'root' })
export class UserService {
  public resourceUrl = '/services/cupo-user-administration/api/users';
  public resourceQueryUrl = '/services/cupo-user-administration/api/users/by-type/CUSTOMER';

  private _avatarImageChanged = new BehaviorSubject<boolean>(false);
  public readonly avatarImageChanged$ = this._avatarImageChanged.asObservable();

  private permissionCache = new Map<Permission, CustomerProfilePermission>;

  constructor(private readonly http: HttpClient, private readonly roleService: RoleService) {
  }

  create(user: IUser): Observable<IUser> {
    return this.http.post<IUser>(this.resourceUrl, user);
  }

  update(user: IUser): Observable<IUser> {
    return this.http.put<IUser>(this.resourceUrl, user);
  }

  find(login: string): Observable<IUser> {
    return this.http.get<IUser>(`${this.resourceUrl}/${login}`);
  }

  findAll(logins: string[]): Observable<IUser[]> {
    // Added catchError to remove error, when showing guidelines in the products-contracts-guidelines component.
    // This is not how it should work. Until someone knows how it should work, this is the solution.
    const data = logins.map(login => {
      return this.find(login).pipe(catchError(err => {
        return EMPTY;
      }));
    });
    return forkJoin([...data]);
  }

  query(req?: SearchWithPagination): Observable<HttpResponse<IUser[]>> {
    const options = createRequestOption(req);
    return this.http.get<IUser[]>(this.resourceQueryUrl, { params: options, observe: 'response' });
  }

  public avatarUrl(login: string, forceRefresh?: boolean): string {
    return SERVER_API_URL + `${this.resourceUrl}/${login}/user-images${forceRefresh ? `?t=${moment.now()}` : ''}`;
  }

  public randomAvatarUrl(): string {
    return SERVER_API_URL + '/api/avatar/png';
  }

  public uploadAvatarImage(login: string, uploadAvatarImageData: FormData): Observable<any> {
    return this.http.post<any>(`${this.resourceUrl}/${login}/user-images/upload`, uploadAvatarImageData, {
      reportProgress: true,
      observe: 'events'
    });
  }

  public uploadAvatarImageBase64(login: string, uploadAvatarImageData: string): Observable<any> {
    return this.http.post<any>(`${this.resourceUrl}/${login}/user-images/upload-base64-png`, uploadAvatarImageData);
  }

  avatarImageChanged(): void {
    this._avatarImageChanged.next(true);
  }

  delete(login: string): Observable<{}> {
    return this.http.delete(`${this.resourceUrl}/${login}`);
  }

  authorities(): Observable<string[]> {
    return this.http.get<string[]>('/services/cupo-user-administration/api/users/authorities');
  }

  customerProfiles(bypassSelectedCustomerProfiles = false): Observable<CustomerProfiles[]> {
    return this.http.get<CustomerProfiles[]>('/services/cupo-user-administration/api/customer-profiles').pipe(
      map(profiles => {
        if (bypassSelectedCustomerProfiles) {
          return profiles;
        } else {
          const selectedCustomerProfileCodes = localStorage.getItem('selected-customer-profile-codes')?.split(',');
          return profiles.filter(
            profile => !selectedCustomerProfileCodes?.length || selectedCustomerProfileCodes.includes(profile.customerProfileCode)
          );
        }
      })
    );
  }

  customerProfilesByPermission(permissionName: Permission): Observable<CustomerProfilePermission> {
    if (this.permissionCache.has(permissionName)) {
      return of(this.filterCustomerProfilePermissionBySelectedCodes(this.permissionCache.get(permissionName)));
    }

    return this.http.get<CustomerProfilePermission>('/services/cupo-user-administration/api/customer-profiles/my-permissions/' + permissionName)
      .pipe(map(customerProfilePermission => {
        this.permissionCache.set(permissionName, customerProfilePermission);
        return this.filterCustomerProfilePermissionBySelectedCodes(customerProfilePermission);
      }));
  }

  private filterCustomerProfilePermissionBySelectedCodes(customerProfilePermission: CustomerProfilePermission): CustomerProfilePermission {
    const selectedCustomerProfileCodes = localStorage.getItem('selected-customer-profile-codes')?.split(',');
    let filteredProfilesPermission = new CustomerProfilePermission();
    filteredProfilesPermission.customerProfiles = customerProfilePermission.customerProfiles
      .filter(profile => !selectedCustomerProfileCodes?.length || selectedCustomerProfileCodes.includes(profile.customerProfileCode))
      .sort((a, b) => a.name.localeCompare(b.name));
    return filteredProfilesPermission;
  }

  syncProfiles(): Observable<void> {
    return this.http.get<void>('/services/cupo-user-administration/api/customer-profiles/sync');
  }
}
